import React from 'react';
import { Box, Typography } from '@mui/material';
import { colors, fonts, borderRadius, spacing } from '../../../constants';

/**
 * MetricCard Component
 * Displays a metric card with label, value, and change indicator
 * 
 * @param {string} label - Metric label
 * @param {string} value - Metric value
 * @param {string} change - Change text (e.g., "2.8% from last month")
 * @param {string} changeType - Change type ('positive' or 'negative')
 * @param {string} animation - Animation name
 * @param {number} animationDelay - Animation delay in seconds
 */
const MetricCard = ({ 
  label, 
  value, 
  change, 
  changeType = 'positive',
  animation,
  animationDelay = 0,
}) => {
  return (
    <Box
      sx={{
        flex: 1,
        backgroundColor: 'rgba(255, 255, 255, 0.12)',
        backdropFilter: 'blur(16px) saturate(180%)',
        WebkitBackdropFilter: 'blur(16px) saturate(180%)',
        borderRadius: borderRadius.lg,
        padding: spacing.sm,
        border: '1px solid rgba(255, 255, 255, 0.25)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        animation: `${animation} 0.6s ease-out ${animationDelay}s both`,
        [`@keyframes ${animation}`]: {
          from: {
            opacity: 0,
            transform: animation === 'slideInLeft' ? 'translateX(-30px)' : 'translateX(30px)',
          },
          to: {
            opacity: 1,
            transform: 'translateX(0)',
          },
        },
        '&:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.2)',
          boxShadow: '0 16px 48px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.4)',
          transform: 'translateY(-4px) scale(1.02)',
          border: '1px solid rgba(255, 255, 255, 0.35)',
        },
      }}
    >
      <Typography
        sx={{
          fontSize: fonts.fontSizeXs,
          color: colors.textWhite,
          mb: '6px',
          fontWeight: fonts.weightMedium,
          opacity: 0.95,
          textShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: '20px',
          fontWeight: fonts.weightBold,
          color: colors.textWhite,
          mb: '4px',
          lineHeight: 1,
          textShadow: '0 2px 8px rgba(0, 0, 0, 0.25)',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'scale(1.05)',
          },
        }}
      >
        {value}
      </Typography>
      <Typography
        sx={{
          fontSize: '10px',
          color: colors.success,
          fontWeight: fonts.weightSemiBold,
          textShadow: '0 1px 3px rgba(16, 185, 129, 0.3)',
        }}
      >
        ↑ {change}
      </Typography>
    </Box>
  );
};

export default MetricCard;
